import { z } from 'zod';

// ═══════════════════════════════════════════════════
//  Purchased pillow insert catalog
// ═══════════════════════════════════════════════════
//
// Two tables, one grid. `pillow_insert_fills` are the columns ("Duck Down
// 10/90", "Poly Cluster"); `pillow_inserts` are the priced cells, each keyed by
// (fill_id, width, height).
//
// There is deliberately no third `sizes` table. The shared size ladder is the
// union of distinct (width, height) across pillow_inserts: a size nobody prices
// does not exist, and an empty cell means that fill does not stock that size —
// a real fact, not a data gap.

// ─── Fills (the matrix columns) ───────────────────────────────────────────

export const PillowInsertFillSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  sort_order: z.number().int(),
  active: z.boolean(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export type PillowInsertFill = z.infer<typeof PillowInsertFillSchema>;

export const CreatePillowInsertFillSchema = z.object({
  name: z.string().min(1, 'Name is required').max(120),
  active: z.boolean().default(true),
});

export const UpdatePillowInsertFillSchema = z
  .object({
    name: z.string().min(1).max(120).optional(),
    sort_order: z.number().int().min(0).optional(),
    active: z.boolean().optional(),
  })
  .strict();

export type CreatePillowInsertFill = z.infer<typeof CreatePillowInsertFillSchema>;
export type UpdatePillowInsertFill = z.infer<typeof UpdatePillowInsertFillSchema>;

// ─── Inserts (the matrix cells) ───────────────────────────────────────────

export const PillowInsertSchema = z.object({
  id: z.string().uuid(),
  fill_id: z.string().uuid(),
  /** Inches. */
  width: z.number().positive(),
  /** Inches. */
  height: z.number().positive(),
  cost_per_each: z.number().min(0),
  /**
   * currency and iva_percent exist for parity with material_library and for a
   * future conversion pass. calcPillowCostPreview reads cost_per_each RAW, the
   * way calcMetalBaseCostPreview reads its rates.
   */
  currency: z.string().min(1),
  iva_percent: z.number().min(0),
  active: z.boolean(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export type PillowInsert = z.infer<typeof PillowInsertSchema>;

export const CreatePillowInsertSchema = z.object({
  fill_id: z.string().uuid(),
  width: z.number().positive(),
  height: z.number().positive(),
  cost_per_each: z.number().min(0),
  currency: z.string().min(1).max(8).default('USD'),
  iva_percent: z.number().min(0).default(0),
  active: z.boolean().default(true),
});

export const UpdatePillowInsertSchema = z
  .object({
    width: z.number().positive().optional(),
    height: z.number().positive().optional(),
    cost_per_each: z.number().min(0).optional(),
    currency: z.string().min(1).max(8).optional(),
    iva_percent: z.number().min(0).optional(),
    active: z.boolean().optional(),
  })
  .strict();

export type CreatePillowInsert = z.infer<typeof CreatePillowInsertSchema>;
export type UpdatePillowInsert = z.infer<typeof UpdatePillowInsertSchema>;

// ─── The whole-matrix save ────────────────────────────────────────────────

/**
 * PUT /pillow-inserts sends every cell the grid is currently showing.
 *
 * `cost_per_each: null` **deletes** that cell — it is the wire form of an empty
 * box, which means "this fill does not stock this size". It is `.nullable()`
 * and NOT `.optional()` on purpose: an omitted key would parse clean and read
 * as a no-op, so a cleared price would silently survive.
 *
 * A cell not named in `rows` is left untouched. Sizes only leave the ladder by
 * having all of their cells blanked, which the client sends as explicit nulls.
 */
export const SavePillowInsertMatrixSchema = z.object({
  rows: z.array(
    z.object({
      fill_id: z.string().uuid(),
      width: z.number().positive(),
      height: z.number().positive(),
      cost_per_each: z.number().min(0).nullable(),
    }),
  ),
});

export type SavePillowInsertMatrix = z.infer<typeof SavePillowInsertMatrixSchema>;
